import { useState, useEffect, useRef } from 'react';
import { Modal } from 'antd';
import type { CheckboxChangeEvent } from 'antd/lib/checkbox';
import type { CheckboxValueType } from 'antd/lib/checkbox/Group';
import {
    CartItemType,
    IBonusProdListData,
    IBonusProduct, IBonusVariant,
    ICartActivityTip,
    SummaryPriceType,
} from './interface';
import { ISummary } from 'base/interfaces/order';
import { COOKIE_CART_SELECTED_IDS, INVALID_BACK_CART_CODES } from 'base/config/constant';
import { setCookie, getCookie } from 'ava-util/dist/cookie';
import { useRouter } from 'next/router';
import { RouteEnum } from 'pro/config/routers';
import { calculateCouponList } from 'mod/coupon/service';
import {
    fetchSummary,
    updateCartItemNum,
    deleteCartItem,
    batchDelete,
    selectCartItem,
    fetchBonusItemList,
} from './services';
import {
    trackAddToCart,
    trackCartImpression,
    trackInitialCheckout,
    trackPageView,
    trackRemoveFromCart
} from 'base/utils/tracker';
import { ICalculateCouponList, ICoupon } from 'mod/coupon/interface';
import { useStore } from 'base/store';
import Message from '@base/lib/Message';
import { postAddCart } from '@base/product/services';
import { msgModal } from '@base/lib/ThemeModal';
import { generatePath } from 'ava-util/dist/request';
import { commonErrorHandler } from 'base/utils/api-callback';
import { fetchCartData } from 'mod/cart/service';
import {
    getGroupItems,
    getGroupsCheckedIds,
    getGroupNeedClearSelectedGroupIds,
    isCheckAll,
    mergeGroupsCheckedIds,
    traverseItems
} from 'mod/cart/utils';
import { ICartGroup, ICartItem, ICartItems, IGroupsCheckedIds, ITraversedData } from 'mod/cart/interface';
import { clone } from 'ava-util/dist/data';
import { useTrans } from 'base/utils/i18n';
import useAriaLive from 'base/hooks/useAriaLive';
import { formatPrice } from 'base/utils/price';
import { checkGroupValidHandle } from 'base/utils/checkout';
import site from 'pro/config/site';

type StatusType = {
    items: ICartItems;
    checkedIds: IGroupsCheckedIds;
    instockIds: string[];
    summaryLoading: boolean;
    summaryWaiting: boolean;
    isSelecting: boolean;    //选中/取消选中请求状态
    cartLoadNext: (() => Promise<void>) | null;
    cartLoading: boolean;
    trackCode: string;//埋点
    revenue: number;//埋点
    isPageLoaded: boolean; //页面是否加载
    bonusProdList: IBonusProduct[];
};

type IUseCartProps = {
    showCartList?: boolean;
    showSummary?: boolean;
    showCoupon?: boolean;
    showBonusProdList?: boolean;
    isMiniCart?: boolean;
    canAriaLive?: boolean; // 是否设则ada， 详情页不设置（mini cart 请求）
};

export type CartType = {
    //list
    activityTips: ICartActivityTip[]; //活动提示
    cartLoading?: boolean;
    groups: ICartGroup[];
    items: ICartItems;
    totalQty: number; //选中商品数量之和
    checkedTotalQty: number; //选中商品数量之和
    onChangeItemNum: (itemId: string, num: number) => void;

    onDeleteItem: (itemId: string, isOos: boolean) => Promise<void>;
    loadCart: (params?: ILoadCartParams) => Promise<void>;
    //checkbox
    checkedIds: IGroupsCheckedIds;
    checkAll: boolean;
    checkAllDisabled: boolean;
    indeterminate: boolean;
    // onCheckIds: (ids: CheckboxValueType[]) => void;
    onCheckAll: (e: CheckboxChangeEvent) => void;
    onGroupCheckAll: (group: ICartGroup, groupCheckAll: boolean) => void;
    onChangeIds: (groupId: string, ids: CheckboxValueType[]) => void;
    //batch
    batchActive: boolean;
    switchBatch: (active: boolean) => void;
    batchCheckedIds: IGroupsCheckedIds;
    onBatchChangeIds: (groupId: string, ids: CheckboxValueType[]) => void;
    onBatchCheckAll: (e: CheckboxChangeEvent) => void;
    onBatchGroupCheckAll: (group: ICartGroup, groupCheckAll: boolean) => void;
    onBatchDelete: () => Promise<void>;

    //summary
    summaryLoading: boolean;
    summaryData: SummaryPriceType;
    summary: ISummary;
    goCheckout: () => Promise<void>;
    // coupon
    isDealer: boolean; //是否经销商
    couponsListData?: ICalculateCouponList;
    // other
    isCartPage: boolean; //是否购物车页面
    // 加购商品
    showBonusProdList: boolean;//是否显示加购商品列表
    bonusProdListData: IBonusProdListData | undefined;
    onBonusProdSelect: (prod: IBonusVariant) => Promise<void>;
};

type ILoadCartParams = {
    loadingType?: 'whole' | 'part';
    isLoadList?: boolean; // 未选中商品操作后不需要重新加载列表
    fromListBonusItem?: boolean; // 来自加购商品列表页面的加购商品列表触发minicart更新后，避免反向更新
}

const getAllIds = (list: CartItemType[]): string[] => {
    return list.map(item => item.id);
};

const trans2StatusList = (list: CartItemType[]): Record<string, CartItemType> => {
    const o: Record<string, CartItemType> = {};
    list.forEach(item => {
        o[item.id] = item;
    });
    return o;
};

const checkSummaryLoading = (items: ICartItems, checkedIds: IGroupsCheckedIds) => {
    let loading: boolean = false;

    for (const id of mergeGroupsCheckedIds(checkedIds)) {
        if (items[id]?.isChangingQty || items[id]?.isDeleting) {
            loading = true;
            break;
        }
    }
    return loading;
};

const emptySummary = {
    totalPrice: 0,
    retailPrice: 0,
    retailDiscount: 0,
    productTotalPrice: 0,
    shipPrice: 0,
    vatPrice: 0,
    couponPrice: 0,
};

const filterDeletedIds = (ids: string[], deletedIds: string[]) => {
    return ids.filter(id => !deletedIds.includes(id));
};

export default function useCart(props: IUseCartProps = {}): CartType {
    const {
        showCartList = true,
        showSummary = true,
        showCoupon = true,
        showBonusProdList = true,
        isMiniCart = false,
        canAriaLive = false
    } = props;

    const { t } = useTrans();
    const ariaLive = useAriaLive({
        id: 'cart-aria-live',
    })
    const { onSetAriaLiveContent } = ariaLive

    const router = useRouter();
    const isCartPage = router.pathname === RouteEnum.CART;
    const { userStore, miniCartStore } = useStore();
    const isDealer = userStore.isDealer();
    const isPhone = process.env.NEXT_PUBLIC_TERMINAL === 'm';
    // 列表
    const [activityTips, setActivityTips] = useState<ICartActivityTip[]>([]); //有库存列表
    const [bonusProdListData, setBonusProdListData] = useState<IBonusProdListData>(); //加购商品列表
    const [groups, setGroups] = useState<ICartGroup[]>([]); //组列表
    const [items, setItems] = useState<ICartItems>({} as ICartItems); //Cart商品列表
    const [checkedIds, setCheckedIds] = useState<IGroupsCheckedIds>({}); //批量操作已选择的item id
    const [cartLoading, setCartLoading] = useState<boolean | undefined>(); //列表加载状态
    const [batchActive, setBatchActive] = useState<boolean>(false); //批量管理状态
    const [totalQty, setTotalQty] = useState<number>(0); //in stock商品数量之和
    const [checkedTotalQty, setCheckedTotalQty] = useState<number>(0); //选中商品数量之和
    // checkbox
    const [checkAll, setCheckAll] = useState<boolean>(false);
    const [indeterminate, setIndeterminate] = useState<boolean>(false);
    const [checkAllDisabled, setCheckAllDisabled] = useState<boolean>(false);
    //batch checkbox
    const [batchCheckedIds, setBatchCheckedIds] = useState<IGroupsCheckedIds>({}); //批量操作已选择的item id
    // summary
    const [summaryLoading, setSummaryLoading] = useState<boolean>(false);
    const [summaryData, setSummaryData] = useState<SummaryPriceType>({ ...emptySummary } as SummaryPriceType);

    // Coupon
    const [couponsListData, setCouponsListData] = useState<ICalculateCouponList>();
    // 保存实时数据, 为解决useEffect闭包造成的state作用域问题，所有请求和判断需要的数据均从实时数据中获取
    const statusRef = useRef<StatusType>({
        items: {} as ICartItems,
        checkedIds: {},
        instockIds: [],
        summaryLoading: false,
        summaryWaiting: false,
        cartLoading: false,
        cartLoadNext: null,
        trackCode: '',
        revenue: 0,
        isPageLoaded: false,
        bonusProdList: [],
        isSelecting: false,
    });

    const loadCart = async (params: ILoadCartParams = {}) => {
        const {
            loadingType = 'part',
            isLoadList = true,
            fromListBonusItem = false,
        } = params;


        const status = statusRef.current;
        if (status.cartLoading) {
            status.cartLoadNext = () => loadCart(params);
            return;
        }
        status.cartLoading = true;

        loadingType === 'whole' ? setCartLoading(true) : setSummaryLoading(true);

        canAriaLive && onSetAriaLiveContent?.('fetch order summary')

        const [
            cartDataRes,
            bonusProdListRes,
            summaryRes,
            couponRes
        ] = await Promise.all([
            (showCartList && isLoadList) ? fetchCartData() : null,
            showBonusProdList ? fetchBonusItemList() : null,
            showSummary ? fetchSummary() : null,
            showCoupon ? calculateCouponList({
                // variantNumList: getVariantNumList(status.list, status.checkedIds),
                page: 'cart'
            }) : null
        ]);

        status.cartLoading = false;
        if (status.cartLoadNext) {
            status.cartLoadNext();
            status.cartLoadNext = null;

        } else {
            const isEditing = checkSummaryLoading(status.items, status.checkedIds) || status.isSelecting;

            if (cartDataRes) {
                if (cartDataRes.groups.length > 0) {

                    const _items = cartDataRes.items;
                    // 判断组是否可选，不可选时清除组内已选的cartId
                    const needClearGroupIds = getGroupNeedClearSelectedGroupIds(_items)
                    if (needClearGroupIds) {
                        const clearGroupsIds = needClearGroupIds.reduce((obj, key)=> {
                            obj[key] = [];
                            return obj;
                        }, {})
                        onCheckIds({
                            ...checkedIds,
                            ...clearGroupsIds
                        }).then()

                        return
                    }

                    if (isEditing) {
                        const statusMergedIds = mergeGroupsCheckedIds(status.checkedIds);
                        for (const itemId in _items) {
                            const statusItem = status.items[itemId];
                            if (statusItem) {
                                _items[itemId].isChangingQty = statusItem.isChangingQty;
                                _items[itemId].isDeleting = statusItem.isDeleting;
                                _items[itemId].num = statusItem.num;
                                _items[itemId].select = statusMergedIds.includes(itemId);
                            }
                        }
                    }
                    const traverse = traverseItems(_items);
                    const _checkedIds = getGroupsCheckedIds(cartDataRes.groups, _items);

                    statusRef.current.items = _items;
                    statusRef.current.checkedIds = _checkedIds;
                    // statusRef.current.instockIds = traverse.instockIds;
                    setGroups(cartDataRes.groups);
                    setItems(_items);
                    setCheckedIds(_checkedIds);
                    renderTraverse(traverse);
                    setActivityTips(cartDataRes.activityTips);

                    setCookie(COOKIE_CART_SELECTED_IDS, JSON.stringify(mergeGroupsCheckedIds(_checkedIds)));
                } else {
                    setGroups([]);
                    setItems({} as ICartItems);
                    setTotalQty(0);
                    setCheckAll(false);
                    setActivityTips([]);
                    setCookie(COOKIE_CART_SELECTED_IDS, JSON.stringify([]));
                }

                // 购物车页面第一次加载数据后打点
                if (isCartPage && !statusRef.current.isPageLoaded) {
                    statusRef.current.isPageLoaded = true;
                    trackPageView({ detail: cartDataRes.track.id });

                    if (Object.values(cartDataRes.items).length > 0 && summaryRes) {
                        trackCartImpression({
                            items: Object.values(cartDataRes.items).map((item, index) => ({
                                ...item,
                                list: `cart-${cartDataRes.cartId}`,
                                shopPrice: item.variantPriceExchange,
                                position: (index + 1),
                            })),
                            orderInfo: {
                                id: cartDataRes.cartId,
                                revenue: Number(summaryRes.totalPrice || 0).toFixed(2),
                            }
                        });
                    }

                }
                statusRef.current.trackCode = cartDataRes.track.id;
            }

            if (bonusProdListRes) {
                setBonusProdListData(bonusProdListRes);
                statusRef.current.bonusProdList = bonusProdListRes.items || [];
            }
            if (summaryRes) {
                setSummaryData(summaryRes);
                if (canAriaLive && summaryRes.totalPrice) {
                    const price1 = formatPrice(summaryRes.totalPrice)
                    await onSetAriaLiveContent?.(
                        `${t('web_summary_order_total_price')}, ${price1.currencySymbol}${price1.integer}${price1.decimal}`,
                        500
                    )
                }
            }
            couponRes && setCouponsListData(couponRes);
            // 检查有没有其他选中item正在删除,改变数量或改变选中状态
            if (!isEditing) {
                setCartLoading(false);
                setSummaryLoading(false);

                // 迷你购物车操作后更新加购商品列表页面
                // TODO: 待优化，购物车提升至全局
                if (isMiniCart && !fromListBonusItem) {
                    document.dispatchEvent(new CustomEvent('loadListBonusItem'));
                }
            }
        }
    };

    // 同时更新实时 items 和 界面items
    const updateItems = (action: 'both' | 'status', itemId: string, newItemData: Partial<ICartItem>) => {
        const status = statusRef.current;
        const _items = clone(status.items);
        _items[itemId] = { ..._items[itemId], ...newItemData }
        if (action === 'both' || action === 'status') {
            status.items = _items;
        }
        if (action === 'both') {
            setItems(_items)
        }
    };
    // 改变数量
    const onChangeItemNum = async (itemId: string, num: number) => {
        await onSetAriaLiveContent(`${num} items`)

        const status = statusRef.current;

        if (status.items[itemId].isChangingQty) {
            updateItems('both', itemId, { num });
            return;
        }
        updateItems('both', itemId, { num, isChangingQty: true });

        // summary 提前设置 loading 状态
        setSummaryLoading(true);

        updateCartItemNum(itemId, num).then(res => {
            updateItems('status', itemId, { isChangingQty: false });

            const item = status.items[itemId];
            const preNum = item.realNum as number;
            if (preNum < num) {
                trackAddToCart([{
                    ...item,
                    shopPrice: item.variantPriceExchange,
                    num: num - preNum,
                }]);
            } else if (preNum > num) {
                trackRemoveFromCart([{
                    ...item,
                    shopPrice: item.variantPriceExchange,
                    num: preNum - num,
                }]);
            }

            item.realNum = num;

            // 请求成功后的值与最新值对比，值发生变化则继续请求
            if (item.num !== num) {
                // 继续请求不需要更新界面loading状态，只更新实时数据
                onChangeItemNum(itemId, item.num);
            } else {
                loadCart();
            }


        }).catch(err => {
            // 请求失败，恢复原数量
            updateItems('both', itemId, {
                num: status.items[itemId].realNum as number,
                isChangingQty: false,
            })
            commonErrorHandler(err, {
                reload: {
                    afterClose: () => {
                        loadCart();
                    }
                }
            });
        });
    };

    // 删除
    const onDeleteItem = async (itemId: string) => {
        const status = statusRef.current;
        // summary 提前设置 loading 状态
        setSummaryLoading(true);

        updateItems('both', itemId, { isDeleting: true });

        try {
            const item = status.items[itemId];
            await deleteCartItem(itemId);
            updateItems('status', itemId, { isDeleting: false });

            loadCart();
            trackRemoveFromCart([{ ...item, shopPrice: item.variantPriceExchange }]);
        } catch (e) {
            console.log(e)
            Message.error(e.message);
            updateItems('both', itemId, { isDeleting: false });
            setSummaryLoading(false);
        } finally {
        }
    };

    //切换批量操作状态
    const switchBatch = (active: boolean) => {
        setBatchActive(active);
        if (active) {
            setBatchCheckedIds({});
        }
    };

    // 勾选
    const onCheckIds = async (groupsCheckedIds: IGroupsCheckedIds) => {
        const status = statusRef.current;
        status.checkedIds = groupsCheckedIds;
        setCheckedIds(groupsCheckedIds);
        const mergedIds = mergeGroupsCheckedIds(groupsCheckedIds);
        setCheckAll(isCheckAll(Object.values(status.items), mergedIds));
        setCheckedTotalQty(mergedIds.length);
        setSummaryLoading(true);
        if (status.isSelecting) {
            return;
        }

        status.isSelecting = true;
        await selectCartItem(mergedIds);
        status.isSelecting = false;
        if (mergedIds.sort().join(',') === mergeGroupsCheckedIds(status.checkedIds).sort().join(',')) {
            loadCart();
        } else {
            onCheckIds(status.checkedIds);
        }
    };

    const onCheckAll = (e: CheckboxChangeEvent) => {
        const checked = e.target.checked;
        const groupMap: IGroupsCheckedIds = {};
        groups.forEach(group => {
            if (checked) {
                const groupItems = getGroupItems(group, items);
                groupMap[group.groupId] = groupItems.filter(item => item.stockNum > 0).map(item => item.id);
            } else {
                groupMap[group.groupId] = [];
            }
        });

        onCheckIds(groupMap);
    };

    const onGroupCheckAll = (group: ICartGroup, groupCheckAll: boolean) => {
        onCheckIds({
            ...checkedIds,
            [group.groupId]: !groupCheckAll ? [] : getGroupItems(group, items).filter(item => item.stockNum > 0).map(item => item.id)
        })
    };

    const onChangeIds = (groupId: string, ids: CheckboxValueType[]) => {
        onCheckIds({
            ...checkedIds,
            [groupId]: ids as string[]
        })
    };


    //batch checkbox
    const onBatchChangeIds = (groupId: string, ids: CheckboxValueType[]) => {
        setBatchCheckedIds({
            ...batchCheckedIds,
            [groupId]: ids as string[]
        })
    };
    const onBatchCheckAll = (e: CheckboxChangeEvent) => {
        const checked = e.target.checked;
        const groupMap: IGroupsCheckedIds = {};
        groups.forEach(group => {
            if (checked) {
                const groupItems = getGroupItems(group, items);
                groupMap[group.groupId] = groupItems.map(item => item.id);
            } else {
                groupMap[group.groupId] = [];
            }
        });

        setBatchCheckedIds(groupMap);
    };

    const onBatchGroupCheckAll = (group: ICartGroup, groupCheckAll: boolean) => {
        setBatchCheckedIds({
            ...batchCheckedIds,
            [group.groupId]: !groupCheckAll ? [] : getGroupItems(group, items).map(item => item.id)
        })
    };
    const onBatchDelete = async () => {
        //@ts-ignore
        Modal.confirm({
            width: 542,
            title: t('web_cart_delete_confirm'),
            icon: null,
            okText: t('web_common_btn_ok'),
            cancelText: t('web_common_btn_cancel'),
            okButtonProps: { size: 'large' },
            cancelButtonProps: { size: 'large' },
            centered: true,
            wrapClassName: 'ant-modal-confirm-title-center',
            onOk: async () => {
                try {
                    const mergedIds = mergeGroupsCheckedIds(batchCheckedIds);

                    await batchDelete(mergedIds);

                    trackRemoveFromCart(Object.values(items).filter(item => mergedIds.includes(item.id)).map(item => ({
                        ...item,
                        shopPrice: item.variantPriceExchange
                    })));

                    setBatchCheckedIds({});
                    if (mergedIds.length === Object.keys(items).length) {
                        setBatchActive(false);
                    }
                    loadCart();

                } catch (e) {
                    Message.error(e.message);
                }
            },
        });
    };

    //页面跳转
    const goCheckout = async () => {
        // 多页面操作判断 保存的cartIds
        const idsString = getCookie(COOKIE_CART_SELECTED_IDS)
        const ids = !!idsString ? JSON.parse(idsString) : []
        const mergedIds = mergeGroupsCheckedIds(checkedIds);

        if (mergedIds.length > 0 && ids.length > 0) {
            try {
                await checkGroupValidHandle(mergedIds);
            } catch (err) {
                if (typeof err.code !== 'undefined' && INVALID_BACK_CART_CODES.includes(err.code)) {
                    msgModal({
                        content: err.message,
                        okText: t('web_common_btn_return_to_cart'),
                        afterClose: () => {
                            loadCart({
                                loadingType: 'whole',
                                isLoadList: true,
                            })
                        },
                    });
                } else {
                    commonErrorHandler(err);
                }
                return;
            }

            trackInitialCheckout({
                items: Object.values(items).map((item, index) => ({
                    ...item,
                    shopPrice: item.variantPriceExchange,
                    position: index,
                })),
                orderInfo: {
                    id: statusRef.current.trackCode,
                    revenue: Number(statusRef.current.revenue || 0).toFixed(2),
                }
            });

            router.push(RouteEnum.CHECKOUT);
        } else {
            loadCart()
        }
    };

    /**
     * 修改 Bonus Prod 选择
     * @param variant
     * @param replaceVariant    可选 删除替换
     */
    const onBonusProdSelect = async (variant: IBonusVariant, replaceVariant?: IBonusVariant) => {
        const { tradeIn, variantId } = variant;
        // 加购使用积分的商品必须登录
        if (!tradeIn.hasSelect && tradeIn.tradeInType === 'points' && !userStore.checkAuth()) {
            msgModal({
                content: t('web_cart_bonus_login_confirm'),
                okText: t('web_common_btn_login'),
                onOk: () => {
                    router.push(generatePath(RouteEnum.LOGIN, { redirect: router.asPath }));
                }
            });
            return;
        }

        // 已选择 无替换
        if (tradeIn.hasSelect && !replaceVariant) {
            try {
                await onDeleteItem(tradeIn.cartItemId);
                isPhone && miniCartStore.afterAddCart(-1);
                loadCart();
            } catch (e) {
                setSummaryLoading(false);
            }

        } else {
            // 有替换选项
            const _deleteProdTradeIn = replaceVariant?.tradeIn

            setSummaryLoading(true);
            try {
                await postAddCart(
                    [{
                        productVariantId: variantId,
                        num: 1,
                    }],
                    'bonusItemTradeIn',
                    _deleteProdTradeIn ? [_deleteProdTradeIn.cartItemId] : []
                );
                loadCart();
                isPhone && miniCartStore.afterAddCart(1);
            } catch (e) {

                msgModal({
                    content: e.message,
                    okText: t('web_common_btn_ok'),
                });
                await loadCart();
                setSummaryLoading(false);
            }
        }
    };

    const renderTraverse = (data: ITraversedData): void => {
        setTotalQty(data.totalQty);
        setCheckedTotalQty(data.checkedTotalQty);
        setCheckAll(data.checkAll);
    }

    const transCartSummary = (summaryData: SummaryPriceType): ISummary => {
        const result: ISummary = {
            details: [
                {
                    key: 'retail',
                    displayName: t('web_summary_retail_price'),
                    displayValue: summaryData.retailPriceExchangeDisplay,
                    value: summaryData.retailPrice,
                    valueExchange: summaryData.retailPriceExchange,
                },
                {
                    key: 'subtotal',
                    displayName: t('web_summary_subtotal'),
                    displayValue: summaryData.totalPriceExchangeDisplay,
                    value: summaryData.totalPrice,
                    valueExchange: summaryData.totalPriceExchange,
                    isTotal: true,
                },
            ]
        }
        if (Number(summaryData.discountPrice) > 0) {
            result.details.push({
                key: 'discount',
                displayName: '',
                displayValue: `${t('web_summary_tip_already_saved')}${summaryData.discountPriceExchangeDisplay}`,
                value: summaryData.discountPrice,
                valueExchange: summaryData.discountPriceExchange,
                highlight: true,
            })
        }

        if (summaryData.canObtainPoints) {
            result.rewards = [
                {
                    tip: t('web_summary_tip_reward_points_tooltip', { brand: site.brand }),
                    title: t('web_summary_tip_reward_points', { brand: site.brand }).replace('<span>{{num}}</span>', '{{value}}'),
                    type: 'points',
                    value: summaryData.canObtainPoints
                }
            ]
        }

        return result;
    }

    //初始化
    useEffect(() => {
        loadCart({
            loadingType: 'whole',
            isLoadList: true,
        });
    }, []);

    return {
        //list
        activityTips,
        groups,
        items,
        totalQty,
        checkedTotalQty,
        onChangeItemNum,
        onDeleteItem,
        loadCart,
        batchActive,
        switchBatch,
        cartLoading,
        //checkbox
        checkedIds,
        checkAll,
        checkAllDisabled,
        indeterminate,
        // onCheckIds,
        onCheckAll,
        onGroupCheckAll,
        onChangeIds,
        //batch checkbox
        batchCheckedIds,
        onBatchChangeIds,
        onBatchCheckAll,
        onBatchGroupCheckAll,
        onBatchDelete,
        //summary
        summaryLoading,
        summaryData,
        summary: transCartSummary(summaryData),
        goCheckout,
        // coupon
        couponsListData,
        //other
        isCartPage,
        isDealer,
        // 加购商品
        showBonusProdList,
        bonusProdListData,
        onBonusProdSelect,
    };
}